import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useGoogleLogin } from "@react-oauth/google";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, Eye, EyeOff, User, Phone, Gift, Loader2, CheckCircle2, ShieldCheck, XCircle, AtSign, PlayCircle } from "lucide-react";
import { NeuralBackground } from "../components/NeuralBackground";
import { startDemoTour } from "../components/DemoAutoTour";

const API = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const [step, setStep] = useState(1); // 1=form, 2=otp, 3=done
  const [form, setForm] = useState({ full_name: "", email: "", username: "", mobile_no: "", country_code: "+91", password: "", confirm_password: "", referral_code: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [usernameStatus, setUsernameStatus] = useState(null); // null | "checking" | "available" | "taken" | "invalid"
  const [usernameMsg, setUsernameMsg] = useState("");
  const [showRegForm, setShowRegForm] = useState(false);
  const otpRefs = useRef([]);
  const emailTimerRef = useRef(null);
  const usernameTimerRef = useRef(null);
  const { registerWithPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSuccess = async (code) => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/api/auth/google-oauth`, { code });
      const { access_token, user, is_new } = res.data;
      localStorage.setItem("tribe_token", access_token);
      toast.success(is_new ? `Welcome to AIxTribe, ${user?.name || "friend"}!` : "Signed in");
      // Track demo → signup conversion if applicable
      const fromDemo = searchParams.get("from") === "demo";
      const demoSid = searchParams.get("sid");
      if (fromDemo && demoSid && is_new) {
        axios.post(`${API}/api/demo/track`, { session_id: demoSid, event: "signup_completed" }).catch(() => {});
      }
      window.location.href = "/feed";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Google sign-up failed");
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: (codeResp) => handleGoogleSuccess(codeResp.code),
    onError: () => toast.error("Google sign-up cancelled"),
  });

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setForm(f => ({ ...f, referral_code: ref }));
  }, [searchParams]);

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  const updateForm = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Auto-send OTP when user enters a valid email (debounced 1.5s)
  const autoSendOtp = useCallback(async (email) => {
    if (!email || !email.includes("@") || !email.includes(".") || emailVerified) return;
    setOtpSending(true);
    try {
      await axios.post(`${API}/api/auth/send-email-otp`, { email: email.trim().toLowerCase() });
      setStep(2);
      setResendTimer(60);
      toast.success("Verification code sent to your email!");
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (detail.includes("already registered")) {
        toast.error("This email is already registered. Please log in.");
      } else if (!detail.includes("wait")) {
        toast.error(detail || "Failed to send OTP");
      }
    }
    setOtpSending(false);
  }, [emailVerified]);

  const handleEmailChange = (val) => {
    updateForm("email", val);
    if (emailVerified) setEmailVerified(false);
    // Debounce auto-send OTP
    if (emailTimerRef.current) clearTimeout(emailTimerRef.current);
    if (val && val.includes("@") && val.includes(".")) {
      emailTimerRef.current = setTimeout(() => autoSendOtp(val), 1500);
    }
  };

  // Username availability check (debounced 500ms)
  const checkUsername = useCallback(async (username) => {
    if (!username || username.length < 3) {
      if (username && username.length > 0) {
        setUsernameStatus("invalid");
        setUsernameMsg("Username must be at least 3 characters");
      } else {
        setUsernameStatus(null);
        setUsernameMsg("");
      }
      return;
    }
    setUsernameStatus("checking");
    setUsernameMsg("");
    try {
      const res = await axios.post(`${API}/api/auth/check-username`, { username });
      if (res.data.available) {
        setUsernameStatus("available");
        setUsernameMsg("Username is available");
      } else {
        setUsernameStatus("taken");
        setUsernameMsg(res.data.message || "Username already taken");
      }
    } catch {
      setUsernameStatus(null);
      setUsernameMsg("");
    }
  }, []);

  const handleUsernameChange = (val) => {
    const clean = val.toLowerCase().replace(/[^a-z0-9_-]/g, "");
    updateForm("username", clean);
    setUsernameStatus(null);
    setUsernameMsg("");
    if (usernameTimerRef.current) clearTimeout(usernameTimerRef.current);
    if (clean.length > 0) {
      usernameTimerRef.current = setTimeout(() => checkUsername(clean), 500);
    }
  };

  const handleSendOtp = async () => {
    if (!form.email || !form.email.includes("@")) {
      toast.error("Enter a valid email first");
      return;
    }
    setOtpSending(true);
    try {
      await axios.post(`${API}/api/auth/send-email-otp`, { email: form.email.trim().toLowerCase() });
      setStep(2);
      setResendTimer(60);
      toast.success("Verification code sent to your email!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send OTP");
    }
    setOtpSending(false);
  };

  const handleOtpChange = (idx, val) => {
    if (val.length > 1) val = val.slice(-1);
    if (val && !/^\d$/.test(val)) return;
    const newOtp = [...otp];
    newOtp[idx] = val;
    setOtp(newOtp);
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) otpRefs.current[idx - 1]?.focus();
  };

  const handleVerifyOtp = async () => {
    const otpStr = otp.join("");
    if (otpStr.length !== 6) { toast.error("Enter all 6 digits"); return; }
    setOtpVerifying(true);
    try {
      await axios.post(`${API}/api/auth/verify-email-otp`, { email: form.email.trim().toLowerCase(), otp: otpStr });
      setEmailVerified(true);
      setStep(1);
      toast.success("Email verified!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid OTP");
    }
    setOtpVerifying(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!emailVerified) { toast.error("Please verify your email first"); return; }
    if (!form.full_name.trim()) { toast.error("Full name is required"); return; }
    if (!form.username.trim() || form.username.length < 3) { toast.error("Username must be at least 3 characters"); return; }
    if (usernameStatus === "taken") { toast.error("Username is already taken"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (form.password !== form.confirm_password) { toast.error("Passwords do not match"); return; }

    setLoading(true);
    try {
      // Track signup-started for demo visitors before the API call
      const fromDemo = searchParams.get("from") === "demo";
      const demoSid = searchParams.get("sid");
      if (fromDemo && demoSid) {
        axios.post(`${API}/api/demo/track`, { session_id: demoSid, event: "signup_started" }).catch(() => {});
      }
      await registerWithPassword(form);
      toast.success("Account created!");
      // Track demo → signup conversion
      if (fromDemo && demoSid) {
        axios.post(`${API}/api/demo/track`, { session_id: demoSid, event: "signup_completed" }).catch(() => {});
      }
      navigate("/feed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center p-4 relative" data-testid="register-page">
      <NeuralBackground />
      <div className="w-full max-w-md relative z-10 flex flex-col items-center">
        <div className="w-36 h-36 md:w-44 md:h-44 rounded-2xl flex items-center justify-center p-4 mb-6"><img src="/brain-logo.png?v=4" alt="AIxTribe" className="w-full h-full object-contain rounded-xl" style={{ mixBlendMode: "screen" }} /></div>

        <h1 className="text-2xl font-bold text-white mb-2 font-heading">Create Account</h1>
        <p className="text-gray-400 text-center mb-8">{showRegForm ? "Sign up with your email" : "Choose how you'd like to sign up"}</p>

        {!showRegForm ? (
          <div className="w-full space-y-4">
            {/* Continue with Google — uses @react-oauth/google popup, code flow. REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button onClick={() => googleLogin()} className="w-full h-14 bg-[#1A1A24] hover:bg-[#222230] border border-[#2A2A3A] rounded-xl flex items-center justify-center gap-3 text-white font-semibold transition-colors" data-testid="google-register-btn">
              <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </button>
            <div className="flex items-center gap-3 my-2"><div className="flex-1 h-px bg-[#2A2A3A]" /><span className="text-gray-600 text-xs">or</span><div className="flex-1 h-px bg-[#2A2A3A]" /></div>
            <button onClick={() => setShowRegForm(true)} className="w-full h-14 bg-[#15151F] hover:bg-[#1A1A24] border border-[#2A2A3A] rounded-xl flex items-center justify-center gap-3 text-gray-300 font-semibold transition-colors" data-testid="email-register-btn">
              <Mail className="w-5 h-5" /> Continue with Email
            </button>
            <p className="text-gray-600 text-xs text-center mt-4">By continuing, you agree to our <Link to="/terms" className="text-gray-400 underline">Terms of Service</Link> and <Link to="/privacy" className="text-gray-400 underline">Privacy Policy</Link>.</p>
            <button onClick={() => startDemoTour()} className="w-full mt-2 inline-flex items-center justify-center gap-2 text-tribe-cyan text-sm hover:text-white transition-colors" data-testid="register-watch-demo">
              <PlayCircle className="w-4 h-4" /> See how AIxTribe works (2 min demo)
            </button>
            <p className="text-gray-500 text-sm mt-4 text-center">Already have an account? <Link to="/login" className="text-tribe-cyan hover:underline">Login here</Link></p>
          </div>
        ) : (
          <div className="w-full">
            <button onClick={() => setShowRegForm(false)} className="text-gray-500 text-sm hover:text-white mb-4 flex items-center gap-1 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to sign up options
            </button>

        {/* OTP Verification Modal */}
        {step === 2 && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#15151F] border border-[#2A2A3A] rounded-2xl w-full max-w-sm p-6" data-testid="otp-modal">
              <div className="text-center mb-5">
                <div className="w-14 h-14 rounded-full bg-tribe-cyan/10 flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-7 h-7 text-tribe-cyan" />
                </div>
                <h2 className="text-lg font-bold text-white">Verify Your Email</h2>
                <p className="text-gray-500 text-sm mt-1">Enter the 6-digit code sent to</p>
                <p className="text-tribe-cyan text-sm font-medium">{form.email}</p>
              </div>

              <div className="flex gap-2 justify-center mb-5">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => otpRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    className="w-11 h-13 text-center text-xl font-bold text-white bg-[#0A0A0F] border border-[#2A2A3A] rounded-lg focus:border-tribe-cyan focus:outline-none transition-colors"
                    data-testid={`otp-input-${i}`}
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              <Button onClick={handleVerifyOtp} disabled={otpVerifying || otp.join("").length !== 6} className="w-full h-11 gradient-cyan-purple text-white font-semibold mb-3" data-testid="verify-otp-btn">
                {otpVerifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Verify
              </Button>

              <div className="flex items-center justify-between text-xs">
                <button onClick={() => { setStep(1); setOtp(["","","","","",""]); }} className="text-gray-500 hover:text-white">
                  Change email
                </button>
                <button onClick={handleSendOtp} disabled={resendTimer > 0 || otpSending} className="text-tribe-cyan disabled:text-gray-600">
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend code"}
                </button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleRegister} className="space-y-3">
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input value={form.full_name} onChange={e => updateForm("full_name", e.target.value)} placeholder="Full Name" className="h-13 pl-12 bg-[#15151F] border-[#2A2A3A] text-white placeholder:text-gray-500 rounded-xl" data-testid="fullname-input" />
          </div>

          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              type="email" value={form.email}
              onChange={e => handleEmailChange(e.target.value)}
              placeholder="Email Address"
              className="h-13 pl-12 pr-28 bg-[#15151F] border-[#2A2A3A] text-white placeholder:text-gray-500 rounded-xl"
              data-testid="email-input"
              disabled={emailVerified}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {emailVerified ? (
                <span className="flex items-center gap-1 text-green-400 text-xs font-semibold px-2 py-1" data-testid="email-verified-badge">
                  <CheckCircle2 className="w-4 h-4" /> Verified
                </span>
              ) : otpSending ? (
                <span className="flex items-center gap-1 text-tribe-cyan text-xs px-2 py-1">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending...
                </span>
              ) : form.email && form.email.includes("@") && form.email.includes(".") ? (
                <span className="flex items-center gap-1 text-gray-500 text-xs px-2 py-1">
                  <Mail className="w-3.5 h-3.5" /> OTP will be sent
                </span>
              ) : null}
            </div>
          </div>

          <div>
            <div className="relative">
              <AtSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input value={form.username} onChange={e => handleUsernameChange(e.target.value)} placeholder="Username" className="h-13 pl-12 pr-12 bg-[#15151F] border-[#2A2A3A] text-white placeholder:text-gray-500 rounded-xl" data-testid="username-input" />
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                {usernameStatus === "checking" && <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
                {usernameStatus === "available" && <CheckCircle2 className="w-4 h-4 text-green-400" />}
                {(usernameStatus === "taken" || usernameStatus === "invalid") && <XCircle className="w-4 h-4 text-red-400" />}
              </div>
            </div>
            {usernameMsg && (
              <p className={`text-xs mt-1.5 ml-1 ${usernameStatus === "available" ? "text-green-400" : "text-red-400"}`} data-testid="username-status-msg">
                {usernameMsg}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <select value={form.country_code} onChange={e => updateForm("country_code", e.target.value)} className="h-13 bg-[#15151F] border border-[#2A2A3A] text-white rounded-xl px-3 w-24 text-sm">
              <option value="+91">+91</option><option value="+1">+1</option><option value="+44">+44</option><option value="+971">+971</option><option value="+61">+61</option><option value="+65">+65</option>
            </select>
            <div className="relative flex-1">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input value={form.mobile_no} onChange={e => updateForm("mobile_no", e.target.value.replace(/\D/g, ""))} placeholder="Mobile Number" className="h-13 pl-12 bg-[#15151F] border-[#2A2A3A] text-white placeholder:text-gray-500 rounded-xl" data-testid="mobile-input" />
            </div>
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input type={showPassword ? "text" : "password"} value={form.password} onChange={e => updateForm("password", e.target.value)} placeholder="Password (min 6 chars)" className="h-13 pl-12 pr-12 bg-[#15151F] border-[#2A2A3A] text-white placeholder:text-gray-500 rounded-xl" data-testid="password-input" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input type={showConfirm ? "text" : "password"} value={form.confirm_password} onChange={e => updateForm("confirm_password", e.target.value)} placeholder="Confirm Password" className="h-13 pl-12 pr-12 bg-[#15151F] border-[#2A2A3A] text-white placeholder:text-gray-500 rounded-xl" data-testid="confirm-password-input" />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
              {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <div className="relative">
            <Gift className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input value={form.referral_code} onChange={e => updateForm("referral_code", e.target.value.toUpperCase())} placeholder="Referral Code (optional)" className="h-13 pl-12 bg-[#15151F] border-[#2A2A3A] text-white placeholder:text-gray-500 rounded-xl" data-testid="referral-input" />
          </div>

          <Button type="submit" disabled={loading || !emailVerified} className="w-full h-14 gradient-cyan-purple text-white font-semibold text-lg rounded-xl btn-glow" data-testid="register-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
            {!emailVerified ? "Verify Email First" : "Create Account"}
          </Button>
        </form>

        <p className="text-gray-500 text-sm mt-6 text-center">
          Already have an account? <Link to="/login" className="text-tribe-cyan hover:underline" data-testid="login-link">Log in here</Link>
        </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterPage;
